import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../../context/ThemeContext';

// Which dates to prompt for each status transition
const STATUS_DATE_CONFIG = {
  leased: {
    fields: [
      { key: 'lease_start_date', label: 'Lease Start Date', required: true, defaultToday: true },
      { key: 'move_in_date', label: 'Expected Move-In', required: false },
    ],
  },
  notice: {
    fields: [
      { key: 'move_out_date', label: 'Expected Move-Out', required: true },
    ],
  },
  occupied: {
    fields: [
      { key: 'move_in_date', label: 'Move-In Date', required: true, defaultToday: true },
    ],
  },
  vacant: {
    fields: [
      { key: 'move_out_date', label: 'Move-Out Date', required: true, defaultToday: true },
    ],
  },
};

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

export default function StatusChangePopover({ unit, newStatus, onConfirm, onCancel, anchorRef }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const popoverRef = useRef(null);

  const config = STATUS_DATE_CONFIG[newStatus];

  // Initialize date values with smart pre-fills
  const [dates, setDates] = useState(() => {
    const initial = {};
    if (!config) return initial;
    config.fields.forEach(f => {
      if (f.defaultToday) {
        initial[f.key] = todayStr();
      } else if (f.key === 'move_in_date' && newStatus === 'occupied' && unit.move_in_date) {
        // Coming from leased → occupied: pre-fill from expected move-in
        initial[f.key] = unit.move_in_date;
      } else if (f.key === 'move_out_date' && newStatus === 'vacant' && unit.move_out_date) {
        // Coming from notice → vacant: pre-fill from expected move-out
        initial[f.key] = unit.move_out_date;
      } else {
        initial[f.key] = '';
      }
    });
    return initial;
  });

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onCancel();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onCancel]);

  // No date config for model/offline — confirm immediately
  if (!config) {
    onConfirm(newStatus, {});
    return null;
  }

  const handleSave = () => {
    const datePayload = {};
    config.fields.forEach(f => {
      if (dates[f.key]) datePayload[f.key] = dates[f.key];
    });
    onConfirm(newStatus, datePayload);
  };

  const handleSkip = () => {
    onConfirm(newStatus, {});
  };

  const statusColors = {
    leased: 'border-blue-500',
    notice: 'border-orange-500',
    occupied: 'border-green-500',
    vacant: 'border-red-500',
  };

  const statusLabels = {
    leased: 'Leased',
    notice: 'Notice',
    occupied: 'Occupied',
    vacant: 'Vacant',
  };

  return (
    <div
      ref={popoverRef}
      className={`absolute z-50 mt-1 w-64 rounded-lg shadow-lg border-t-2 ${statusColors[newStatus] || ''} ${
        isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
      }`}
      style={{ right: 0 }}
    >
      <div className="p-3">
        <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Set to <span className="font-semibold">{statusLabels[newStatus]}</span> — Unit {unit.unit_number}
        </p>

        <div className="space-y-2">
          {config.fields.map(f => (
            <div key={f.key}>
              <label className={`block text-[10px] font-medium mb-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {f.label}{f.required && ' *'}
              </label>
              <input
                type="date"
                value={dates[f.key] || ''}
                onChange={(e) => setDates(d => ({ ...d, [f.key]: e.target.value }))}
                className={`w-full px-2 py-1.5 rounded border text-xs ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'
                }`}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSkip}
            className={`text-xs ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Skip dates
          </button>
          <div className="flex gap-1.5">
            <button
              onClick={onCancel}
              className={`px-2.5 py-1 rounded text-xs ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-2.5 py-1 rounded text-xs bg-blue-600 text-white hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper: get which date fields are relevant for a status (for edit modal highlighting)
export function getRelevantDateFields(status) {
  const config = STATUS_DATE_CONFIG[status];
  if (!config) return [];
  return config.fields.map(f => f.key);
}
